import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../component/Header";
import DiamondsFour from "../../component/SvgIcons/DiamondsFour";
import api from "../../services/axios";
import { useSwal } from "../../services/SweetAlert";
import CustomDropdown from "../../component/CustomDropdown";
import { useAuth } from "../../context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import CKEditorWrapper from "../../component/RichTextEditorFile";
import IconError from "../../component/SvgIcons/IconError";

export default function CreateTemplate() {
  const { authUser } = useAuth();
  const { alert } = useSwal();
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [appType, setAppType] = useState([]);

  const [formData, setFormData] = useState({
    Query: "",
    Remarks: "",
    Issue_Details: "",
    Application_Name: "",
  });

  useEffect(() => {
    if (authUser?.Code) {
      setFormData((prev) => ({ ...prev, User: authUser.Code }));
    }
  }, [authUser]);

  useEffect(() => {
    const fetchAppTypes = async () => {
      try {
        const res = await api.post("/Application_Types");
        if (res.data.data && Array.isArray(res.data.data)) {
          const transformed = res.data.data.map((item) => ({
            value: item.Id,
            label: item.DisplayName,
          }));

          setAppType([
            { value: "", label: "Select Application Type", disabled: true },
            ...transformed,
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch application types:", error);
      }
    };
    fetchAppTypes();
  }, []);

  const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, "").trim();
};
  const getFieldError = (name, value) => {
    switch (name) {
      case "Application_Name":
        return !value ? "Application Type is required." : "";
      case "Query":
        return !value?.trim() ? "Query is required." : "";
      case "Issue_Details":
        return !value?.trim() ? "Issue details are required." : "";
      case "Remarks":
      return !stripHtml(value) ? "Remarks are required." : "";
      default:
        return "";
    }
  };

  const validateField = (name, value) => {
    const error = getFieldError(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleChange = (eOrName, maybeValue) => {
    if (typeof eOrName === "string") {
      // CustomDropdown or manual call: handleChange("Field", value)
      setFormData((prev) => ({ ...prev, [eOrName]: maybeValue }));
      validateField(eOrName, maybeValue);
    } else {
      const { name, value } = eOrName.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      validateField(name, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields on submit
    const requiredFields = ["Application_Name", "Query", "Issue_Details", "Remarks"];
    const newErrors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = getFieldError(field, formData[field]);
      if (error) {
        isValid = false;
        newErrors[field] = error;
      }
    });

    setFormErrors(newErrors);

    if (!isValid) {
      await alert("error", "Please fill in all required fields correctly.");
      return;
    }

    const payload = {
      App_Type: formData.Application_Name || "", // Optional fallback
      Query: formData.Query,
      Issue: formData.Issue_Details,
      Remarks: formData.Remarks,
    };

    try {
      setLoading(true);
      const res = await api.post("/TemplateCreate", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      await alert("success", "Template created successfully.");

      // Reset the form
      setFormData({
        Query: "",
        Issue_Details: "",
        Remarks: "",
        Application_Name: "",
      });
      setFormErrors({});
    } catch (err) {
      const message = err.response?.data?.message;
      if (message) {
        await alert("warning", message);
      } else {
        await alert("error", "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <Header title subtitle="Create Template" searchbar />
      <div className="center-content">
        <div className="container">
          <div className="pagegap d-flex flex-column">
            <h2 className="create-titles mb-0 d-flex align-items-center">
              <DiamondsFour /> <span>Create Template</span>
            </h2>

            <AnimatePresence mode="wait">
              <motion.form
                className="createticket myform d-flex flex-column justify-content-between"
                style={{ height: "auto" }}
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className="create-form d-flex flex-column">
                  <div className="form-row d-flex justify-content-start justify-content-lg-between flex-wrap flex-xl-nowrap flex-column flex-lg-row">
                    <div
                      className={`form-group ${
                        formErrors.Application_Name ? "fieldrequire" : ""
                      }`}
                    >
                      <label>
                        Select Application Type{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <CustomDropdown
                        options={appType}
                        placeholder="Select Application Type"
                        name="Application_Name"
                        value={formData.Application_Name}
                        onChange={(val) => handleChange("Application_Name", val)}
                      />
                     <small className="hide" style={{right:"45px"}}>
                        <IconError/>
                      </small>
                    </div>

                    <div
                      className={`form-group ${
                        formErrors.Query ? "fieldrequire" : ""
                      }`}
                    >
                      <label>
                        Query <span className="required-asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        name="Query"
                        className="form-control"
                        placeholder="Enter Query"
                        value={formData.Query}
                        onChange={handleChange}
                      />
                     <small className="hide">
                        <IconError />
                      </small>
                    </div>
                  </div>

                  <div className={`form-group texteditor d-block ${ formErrors.Issue_Details ? "fieldrequire" : ""}`}>
                    <label>
                      Issue <span className="required-asterisk">*</span>
                    </label>
                    <CKEditorWrapper
                      value={formData.Issue_Details}
                      onChange={(html) => handleChange("Issue_Details", html)}
                    />
                    <small className="hide">
                      <IconError/>
                    </small>
                  </div>

                <div className={`form-group texteditor d-block ${formErrors.Remarks ? "fieldrequire" : ""}`}>
                    <label>
                      Remarks <span className="required-asterisk">*</span>
                    </label>
                    <CKEditorWrapper
                      value={formData.Remarks}
                      onChange={(html) => handleChange("Remarks", html)}
                    />
                    <small className="hide">
                      <IconError/>
                    </small>
                  </div>
                </div>

                <div className="form-savebtns d-flex justify-content-end flex-row align-items-center">
                  <div className="d-flex gap-3">
                    <Link to="#" className="btn cancelbtn">
                      Close
                    </Link>
                    <button
                      type="submit"
                      className="btn btngradiant"
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </motion.form>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
